import React, { useEffect, useRef, useState } from "react";
import "bulma/css/bulma.min.css";
import { Link, useLocation } from "react-router-dom";
import { Check } from "lucide-react";
import "./Nav.css";

const STEPS = [
  { number: 1, path: "/1", label: "Schließplan erstellen" },
  { number: 2, path: "/2", label: "Systemauswahl" },
  { number: 3, path: "/3", label: "Zusammenfassung" },
  { number: 4, path: "/4", label: "Bestellung" },
];

// Konfigurator zapisuje wartości w localStorage jako JSON (np. "\"BKS\"")
const hasStoredValue = (key: string): boolean => {
  const raw = localStorage.getItem(key);
  if (!raw) return false;
  try {
    return String(JSON.parse(raw)).trim() !== "";
  } catch {
    return raw.trim() !== "";
  }
};

const Navbar = () => {
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Detekcja szerokości ekranu
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 960);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Otwarte menu zamyka dotknięcie poza nagłówkiem i klawisz Escape
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileMenuOpen]);

  // Panel administracyjny ma własny pasek boczny z logo - pasek kroków konfiguratora jest tam zbędny
  const isAdminRoute = location.pathname === "/admin";

  const currentStep = STEPS.find((step) => step.path === location.pathname)?.number ?? 0;
  // Zusammenfassung i Bestellung nie mają sensu bez wybranego systemu - strona 3 i tak przekierowałaby na 2
  const systemSelected = hasStoredValue("supllier") && hasStoredValue("system1");

  const renderStep = (step: (typeof STEPS)[number]) => {
    const state = step.number === currentStep ? "current" : step.number < currentStep ? "done" : "upcoming";
    const locked = step.number >= 3 && !systemSelected && state !== "current";
    const className = `nav-step nav-step--${state}${locked ? " nav-step--locked" : ""}`;
    const content = (
      <>
        <span className="nav-step-circle" aria-hidden="true">
          {state === "done" ? <Check size={14} strokeWidth={3} /> : step.number}
        </span>
        <span className="nav-step-label">{step.label}</span>
        {locked && isMobile && <span className="nav-step-hint">erst nach Systemauswahl</span>}
      </>
    );

    if (locked) {
      return (
        <span className={className} aria-disabled="true" title="Bitte wählen Sie zuerst ein Schließsystem aus.">
          {content}
        </span>
      );
    }
    return (
      <Link
        to={step.path}
        className={className}
        title={step.label}
        aria-current={state === "current" ? "step" : undefined}
      >
        {content}
      </Link>
    );
  };

  if (isAdminRoute) return null;

  return (
    <nav
      ref={navRef}
      id="navbar"
      className="navbar is-fixed-top"
      role="navigation"
      aria-label="main navigation"
      style={{
        background: "white",
        borderBottom: isMobile ? "1px solid #e5e5e5" : undefined,
        zIndex: 101,
      }}
    >
      <div className="navbar-menu" style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%", padding: isMobile ? "0 8px" : undefined }}>
        <div className="navbar-start" style={{ flex: "0 0 auto" }}></div>

        <div className="navbar-start" style={{ width: isMobile ? "100%" : "75%", display: "flex", alignItems: "center", justifyContent: isMobile ? "space-between" : undefined }}>
          <div className="navbar-brand" style={{ display: "flex", alignItems: "center" }}>
            {/* Nie linkujemy na "/" - ta trasa zaczyna nową konfigurację i czyści zapisane dane */}
            <Link to="/1">
              <img src="/logo-wplusm-2026.svg" style={{ height: '2.1rem', padding: '3px' }} alt="WplusM" />
            </Link>
          </div>

          {isMobile ? (
            <div className="nav-mobile-actions">
              {currentStep > 0 && (
                <span className="nav-mobile-step-text">
                  Schritt {currentStep} von {STEPS.length}
                </span>
              )}
              <button
                aria-label="menu"
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen((open) => !open)}
                // margines brał się wcześniej z CSS panelu admina (reguła "button" bez zakresu)
                style={{ background: "none", border: "none", borderRadius: 6, padding: 0, margin: "4px 4px 4px 8px", fontSize: 14, cursor: "pointer" }}
              >
                <span style={{ display: "block", width: 28, height: 4, background: "#005CA9", margin: "5px 0", borderRadius: 2 }}></span>
                <span style={{ display: "block", width: 28, height: 4, background: "#005CA9", margin: "5px 0", borderRadius: 2 }}></span>
                <span style={{ display: "block", width: 28, height: 4, background: "#005CA9", margin: "5px 0", borderRadius: 2 }}></span>
              </button>
            </div>
          ) : (
            <ol className="nav-stepper">
              {STEPS.map((step, index) => (
                <React.Fragment key={step.number}>
                  {index > 0 && (
                    <li
                      className={`nav-step-connector${step.number <= currentStep ? " nav-step-connector--done" : ""}`}
                      aria-hidden="true"
                    />
                  )}
                  <li>{renderStep(step)}</li>
                </React.Fragment>
              ))}
            </ol>
          )}
        </div>
      </div>

      {isMobile && currentStep > 0 && (
        <div className="nav-mobile-progress" aria-hidden="true">
          {STEPS.map((step) => (
            <span key={step.number} className={step.number <= currentStep ? "is-filled" : undefined} />
          ))}
        </div>
      )}

      {isMobile && mobileMenuOpen && (
        <ol className="nav-mobile-menu">
          {STEPS.map((step) => (
            <li key={step.number}>{renderStep(step)}</li>
          ))}
        </ol>
      )}
    </nav>
  );
};

export default Navbar;
